import * as ImagePicker from "expo-image-picker";
import { useState, useCallback, useRef, useEffect } from "react";
import { Alert, Linking, Platform } from "react-native";

import { describeCameraFailure, describeCameraPermission } from "@/lib/camera-permission";
import { MOBILE_DEBUG_VERSION } from "@/lib/debug";
import { submitUpload, confirmUpload } from "@/lib/onboarding-api";
import { SkipRules } from "@/lib/recorder/SkipRules";
import { describeUploadFailure } from "@/lib/upload-flow-error";
import { uploadFromUri } from "@/lib/upload-from-uri";
import {
    BATCH_UPLOAD_DEADLINE_MS,
    UPLOAD_TIMEOUT_MS,
    UploadTimeoutError
} from "@/lib/upload-timeout";
import { pickPdfDocument } from "@/utils/pick-upload";
import { resizeImageIfNeeded, toJpeg } from "@/utils/resize-image";

/**
 * Onboarding upload flow — staged images (camera or photo library) and
 * staged PDFs share this queue. Each staged entry tracks the source kind
 * so `uploadAll` can preserve the original `contentType` + filename
 * instead of pretending every upload is a JPEG.
 */
export interface StagedImage {
    id: string;
    uri: string;
    /** Identifies how we resolve the upload payload at flush time. */
    kind: "image" | "pdf";
    /** Only present for PDFs — propagated to S3 + the DB row. */
    contentType?: string;
    /** Only present for PDFs — preserves the user's original filename. */
    filename?: string;
}

/**
 * Custom-upload contract (the medications hub). The hook owns the S3 PUT loop so
 * the staging sheet stays open with a visible "uploading to cloud" state until
 * every image is up — instead of closing the sheet and uploading invisibly,
 * which let an app-suspend kill the in-flight PUT (prod visit 505). The consumer
 * only: (1) resolves presigned URLs for N filenames, and (2) runs its
 * post-upload side effect once ALL images succeed, receiving the s3Keys in
 * staging order. A single failed PUT throws -> hard-fail (the whole batch errors,
 * the sheet closes, nothing is extracted) and keeps staged images in memory so
 * the RN can reopen the sheet and retry.
 */
export interface CustomUploadAdapter {
    getUrls: (filenames: string[]) => Promise<{ urls: { uploadUrl: string; s3Key: string }[] }>;
    onAllUploaded: (s3Keys: string[]) => Promise<void>;
    /** Called when the upload (or URL fetch) hard-fails so the consumer can show
     *  its error UI. The sheet is already closed, but staged images are retained. */
    onUploadError: (err: unknown) => void;
}

interface UseUploadFlowOptions {
    rnId: string;
    submissionId?: string;
    onComplete?: () => void;
    /**
     * Medications path. When provided, the hook uploads each image to S3 via
     * presigned URLs (streaming `uploadFromUri`) WHILE the sheet stays open, then
     * calls `onAllUploaded(s3Keys)`. Replaces the onboarding default upload logic.
     */
    customUpload?: CustomUploadAdapter;
    /**
     * Cap longest image dimension to this many pixels via on-device resize.
     * Skipped if unset. Used by medication upload to keep extraction payloads small
     * (matches v1 production's pattern).
     */
    maxDimension?: number;
    /**
     * Native photo-library selection limit. Pass 0 for no app-side cap.
     */
    gallerySelectionLimit?: number;
}

export function useUploadFlow({
    rnId,
    submissionId,
    onComplete,
    customUpload,
    maxDimension,
    gallerySelectionLimit = 10
}: UseUploadFlowOptions) {
    const [stagedImages, setStagedImages] = useState<StagedImage[]>([]);
    const [showStaging, setShowStaging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState("");
    // Onboarding path only — surfaces a failed uploadAll() to the caller instead
    // of letting it propagate to the modal's Sentry-only catch-all (see the
    // 2026-09 UploadTimeoutError regression: a timed-out PUT silently cleared
    // stagedImages and closed the sheet with no error shown to the RN).
    const [uploadError, setUploadError] = useState<string | null>(null);
    // Real 0..1 completion across the whole batch, counting bytes of the file in
    // flight. `uploadProgress` (the string) stays exactly as it was so existing
    // consumers, notably the onboarding path, are unaffected.
    const [uploadFraction, setUploadFraction] = useState<number | null>(null);
    // Guards a late upload callback from flipping state after unmount.
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const openCamera = useCallback(async () => {
        if (Platform.OS === "web") {
            // No native camera surface on web — open a real file <input> with the
            // capture hint. (expo-image-picker's synthetic click on a hidden input
            // is suppressed in the deployed PWA, esp. iOS Safari.)
            const files = await pickWebFiles({ accept: "image/*", capture: true });
            if (files.length === 0) {
                return;
            }
            setStagedImages((prev) => [...prev, ...stageWebFiles(files)]);
            setShowStaging(true);
            return;
        }
        try {
            const permission = await ImagePicker.requestCameraPermissionsAsync();
            console.info(`[mobile ${MOBILE_DEBUG_VERSION}] upload flow camera permission result`, {
                granted: permission.granted,
                canAskAgain: permission.canAskAgain
            });
            // A bare `return` here is what made this button look broken: the RN
            // taps "Take a photo", nothing opens, and nothing explains why.
            const outcome = describeCameraPermission(permission);
            if (outcome.kind === "denied") {
                Alert.alert(
                    "Camera unavailable",
                    outcome.message,
                    outcome.offerSettings
                        ? [
                              { text: "Not now", style: "cancel" },
                              {
                                  text: "Open Settings",
                                  onPress: () => {
                                      Linking.openSettings().catch(() => {});
                                  }
                              }
                          ]
                        : [{ text: "OK" }]
                );
                return;
            }

            // Visit Recording — pause capture while the camera is open.
            // Camera surfaces don't snapshot reliably + the photo is already
            // uploaded to S3 separately, so the player can render the actual
            // captured image at the gap window.
            SkipRules.notify("camera_open");
            let result;
            try {
                result = await ImagePicker.launchCameraAsync({
                    mediaTypes: ["images"],
                    quality: 0.7
                });
            } finally {
                SkipRules.clear("camera_open");
            }

            console.info(`[mobile ${MOBILE_DEBUG_VERSION}] upload flow camera result`, {
                canceled: result.canceled,
                assets: result.canceled ? 0 : result.assets.length
            });

            if (result.canceled || result.assets.length === 0) {
                return;
            }

            const newImages = await stageAssets(result.assets, maxDimension);
            setStagedImages((prev) => [...prev, ...newImages]);
            setShowStaging(true);
        } catch (err) {
            console.error(`[mobile ${MOBILE_DEBUG_VERSION}] upload flow camera failed`, err);
            // Reaching here means the camera surface refused to open at all —
            // most often a simulator, which has no camera but DOES grant the
            // permission, so the check above waves it through. Silence here is
            // the same dead button by another route.
            Alert.alert("Camera unavailable", describeCameraFailure(err));
        }
    }, [maxDimension]);

    const openGallery = useCallback(async () => {
        //  Web has no native picker; the PWA path stages files directly.
        if (Platform.OS === "web") {
            const files = await pickWebFiles({ accept: "image/*", multiple: true });
            if (files.length === 0) {
                return;
            }
            setStagedImages((prev) => [...prev, ...stageWebFiles(files)]);
            setShowStaging(true);
            return;
        }
        try {
            //  Merge: main's options are kept in full. `Compatible` makes iOS
            //  hand back a JPEG-compatible representation instead of HEIC,
            //  which is the same class of fix as the transcode below, applied
            //  one layer earlier.
            const result = await ImagePicker.launchImageLibraryAsync({
                mediaTypes: ["images"],
                quality: 0.7,
                allowsMultipleSelection: true,
                selectionLimit: gallerySelectionLimit,
                orderedSelection: true,
                preferredAssetRepresentationMode:
                    ImagePicker.UIImagePickerPreferredAssetRepresentationMode.Compatible
            });

            console.info(`[mobile ${MOBILE_DEBUG_VERSION}] upload flow gallery result`, {
                canceled: result.canceled,
                assets: result.canceled ? 0 : result.assets.length
            });

            if (result.canceled || result.assets.length === 0) {
                return;
            }

            const newImages = await stageAssets(result.assets, maxDimension);
            setStagedImages((prev) => [...prev, ...newImages]);
            setShowStaging(true);
        } catch (err) {
            console.error(`[mobile ${MOBILE_DEBUG_VERSION}] upload flow gallery failed`, err);
            customUpload?.onUploadError(err);
        }
    }, [customUpload, gallerySelectionLimit, maxDimension]);

    /**
     * Open the system file picker scoped to PDFs and stage one. PDFs skip
     * the on-device resize step — `maxDimension` only applies to JPEGs.
     */
    const openDocument = useCallback(async () => {
        if (Platform.OS === "web") {
            const files = await pickWebFiles({ accept: "application/pdf", multiple: true });
            if (files.length === 0) {
                return;
            }
            setStagedImages((prev) => [...prev, ...stageWebFiles(files)]);
            setShowStaging(true);
            return;
        }
        const result = await pickPdfDocument();
        if (result.status !== "picked") {
            return;
        }
        // The PDF picker is now multi-select — push every chosen PDF onto
        // the staging list in one shot. Each gets a stable local id so the
        // staging modal's "remove" affordance can target a specific entry.
        setStagedImages((prev) => [
            ...prev,
            ...result.value.map((picked, i) => ({
                id: `${Date.now()}_${i}_${Math.random().toString(36).slice(2, 6)}`,
                uri: picked.uri,
                kind: "pdf" as const,
                contentType: picked.contentType,
                filename: picked.filename
            }))
        ]);
        setShowStaging(true);
    }, []);

    const removeStaged = useCallback((id: string) => {
        setStagedImages((prev) => {
            const next = prev.filter((img) => img.id !== id);
            if (next.length === 0) {
                setShowStaging(false);
            }
            return next;
        });
    }, []);

    const cancelStaging = useCallback(() => {
        setStagedImages([]);
        setShowStaging(false);
        setUploadError(null);
    }, []);

    const reopenStaging = useCallback(() => {
        if (stagedImages.length > 0) {
            setShowStaging(true);
        }
    }, [stagedImages.length]);

    const uploadAll = useCallback(async () => {
        if (stagedImages.length === 0) {
            return;
        }

        // Medications path: KEEP THE SHEET OPEN and upload each image here so the
        // RN watches a real "uploading to cloud" state (app stays foregrounded ->
        // the OS is far less likely to suspend mid-PUT). HARD-FAIL: any single
        // failure aborts the whole batch, closes the sheet, and reports via
        // onUploadError; nothing is extracted. Staged photos stay in memory for
        // a clean retry. Self-contained (own try/catch) — does NOT run
        // the onboarding finally below.
        if (customUpload) {
            setUploading(true);
            const batch = [...stagedImages];
            const filenames = batch.map((img, i) => img.filename ?? `photo-${i}.jpg`);
            try {
                const { urls } = await customUpload.getUrls(filenames);
                // Whole-batch deadline on top of the per-file one. Bounds the
                // total wait however the stall is distributed across the batch,
                // so a long run of slow-but-not-timed-out PUTs can't strand the
                // handoff either.
                const batchStartedAt = Date.now();
                for (let i = 0; i < batch.length; i++) {
                    if (mountedRef.current) {
                        setUploadProgress(`Uploading ${i + 1} of ${batch.length}…`);
                    }
                    const remaining = BATCH_UPLOAD_DEADLINE_MS - (Date.now() - batchStartedAt);
                    if (remaining <= 0) {
                        throw new UploadTimeoutError(BATCH_UPLOAD_DEADLINE_MS);
                    }
                    const img = batch[i];
                    const contentType =
                        img.kind === "pdf" ? (img.contentType ?? "application/pdf") : "image/jpeg";
                    await uploadFromUri({
                        uri: img.uri,
                        uploadUrl: urls[i].uploadUrl,
                        contentType,
                        timeoutMs: Math.min(UPLOAD_TIMEOUT_MS, remaining),
                        onProgress: (fileFraction) => {
                            if (!mountedRef.current) {
                                return;
                            }
                            // Files completed, plus how far into the current one.
                            setUploadFraction((i + (fileFraction ?? 0)) / batch.length);
                        }
                    });
                    if (mountedRef.current) {
                        setUploadFraction((i + 1) / batch.length);
                    }
                }
                // All images up → close the sheet, hand off to extraction.
                const s3Keys = urls.map((u) => u.s3Key);
                if (mountedRef.current) {
                    setShowStaging(false);
                    setUploading(false);
                    setUploadProgress("");
                    setUploadFraction(null);
                }
                // Clear the staged batch only once the handoff has actually
                // succeeded. Clearing before the await (the old order) meant a
                // failed handoff left "Try Again (same photos)" with no photos
                // to retry, so the RN had to re-shoot the whole medicine cabinet.
                await customUpload.onAllUploaded(s3Keys);
                if (mountedRef.current) {
                    setStagedImages([]);
                }
            } catch (err) {
                // Hard-fail: close the sheet and let the consumer show its error.
                // Keep stagedImages so Retry can reopen the same batch.
                if (mountedRef.current) {
                    setShowStaging(false);
                    setUploading(false);
                    setUploadProgress("");
                    setUploadFraction(null);
                }
                customUpload.onUploadError(err);
            }
            return;
        }

        // Onboarding path (documents/PDFs): uploads here with its own
        // submitUpload/confirmUpload flow and keeps the sheet open via `uploading`.
        setUploading(true);
        setUploadError(null);

        // How many of this batch are fully uploaded and confirmed. If the batch
        // fails part-way, those leave the sheet so a retry sends only the rest
        // (it used to re-send them, creating duplicate uploads).
        let sent = 0;
        try {
            for (let i = 0; i < stagedImages.length; i++) {
                setUploadProgress(`Uploading ${i + 1} of ${stagedImages.length}...`);
                const staged = stagedImages[i];

                // Resolve the actual contentType + filename for this entry.
                // Images: expo-image-picker at quality < 1.0 always emits
                // JPEG, so we hardcode `image/jpeg` (the on-device resize in
                // `stageAssets` doesn't change the encoding). PDFs preserve
                // the picked file's own contentType + name so the admin
                // viewer can detect format and skip the AI image pipeline.
                // Prefer the staged entry's own contentType/filename when present
                // (web-picked files + PDFs carry them); otherwise fall back to the
                // native image default (expo emits JPEG at quality < 1.0).
                const contentType =
                    staged.contentType ??
                    (staged.kind === "pdf" ? "application/pdf" : "image/jpeg");
                const filename =
                    staged.filename ??
                    (staged.kind === "pdf"
                        ? `document_${Date.now()}_${i}.pdf`
                        : `document_${Date.now()}_${i}.jpg`);

                const { upload, uploadUrl } = await submitUpload(rnId, {
                    filename,
                    contentType,
                    submissionId
                });

                await uploadFromUri({ uri: staged.uri, uploadUrl, contentType });

                await confirmUpload(rnId, upload.id);
                sent = i + 1;
            }

            onComplete?.();
            if (mountedRef.current) {
                setStagedImages([]);
                setShowStaging(false);
            }
        } catch (err) {
            // Hard-fail, same contract as the medications branch above: keep the
            // sheet open and stagedImages intact so the RN can retry without
            // re-picking every file, and show why instead of silently vanishing.
            console.error(`[mobile ${MOBILE_DEBUG_VERSION}] onboarding upload failed`, err);
            if (mountedRef.current) {
                if (sent > 0) {
                    setStagedImages(stagedImages.slice(sent));
                }
                setUploadError(describeUploadFailure(err, sent));
            }
        } finally {
            if (mountedRef.current) {
                setUploading(false);
                setUploadProgress("");
                setUploadFraction(null);
            }
        }
    }, [stagedImages, rnId, submissionId, onComplete, customUpload]);

    return {
        stagedImages,
        showStaging,
        uploading,
        uploadProgress,
        uploadFraction,
        uploadError,
        openCamera,
        openGallery,
        openDocument,
        reopenStaging,
        removeStaged,
        cancelStaging,
        uploadAll
    };
}

/**
 * Web-only file picker. The deployed PWA cannot use the native camera/gallery,
 * and expo's web shim relies on a synthetic click of a hidden <input> that the
 * browser (notably iOS Safari) refuses to honor. So on web we open a real
 * <input type=file> synchronously inside the tap gesture.
 */
function pickWebFiles(opts: {
    accept: string;
    multiple?: boolean;
    capture?: boolean;
}): Promise<File[]> {
    return new Promise((resolve) => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = opts.accept;
        if (opts.multiple) {
            input.multiple = true;
        }
        if (opts.capture) {
            input.setAttribute("capture", "environment");
        }
        input.style.display = "none";
        input.onchange = () => {
            const files = input.files ? Array.from(input.files) : [];
            input.remove();
            resolve(files);
        };
        // iOS Safari only fires `change` for an <input> that is in the DOM.
        document.body.appendChild(input);
        input.click();
    });
}

/** Stage web-picked File objects, preserving their real contentType + filename. */
function stageWebFiles(files: File[]): StagedImage[] {
    return files.map((file, i) => ({
        id: `${Date.now()}_${i}_${Math.random().toString(36).slice(2, 6)}`,
        uri: URL.createObjectURL(file),
        kind: file.type === "application/pdf" ? ("pdf" as const) : ("image" as const),
        contentType: file.type || "application/octet-stream",
        filename: file.name
    }));
}

async function stageAssets(
    assets: ImagePicker.ImagePickerAsset[],
    maxDimension: number | undefined
): Promise<StagedImage[]> {
    const staged: StagedImage[] = [];
    for (const asset of assets) {
        let uri = asset.uri;
        try {
            //  Both paths emit JPEG: iOS picks are often HEIC, which S3
            //  consumers (Rekognition face-compare, AI extraction, the PDF
            //  pipeline) reject outright. Resize when a cap is given, otherwise
            //  transcode only — the transcode must happen either way.
            uri =
                maxDimension !== undefined
                    ? await resizeImageIfNeeded(asset, maxDimension)
                    : await toJpeg(asset.uri);
        } catch (err) {
            //  Main's resilience, widened to cover the transcode too: a picker
            //  asset we cannot re-encode is still better uploaded as-is than
            //  dropped on the floor.
            console.warn(
                `[mobile ${MOBILE_DEBUG_VERSION}] upload flow image transcode failed; using original asset`,
                {
                    fileName: asset.fileName,
                    width: asset.width,
                    height: asset.height,
                    uri: asset.uri,
                    error: err
                }
            );
        }
        staged.push({
            id: `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
            uri,
            kind: "image" as const,
            filename: asset.fileName ?? undefined
        });
    }
    return staged;
}
